import { useEffect, useState } from 'react';
import { TextField, Dialog, DialogContent, DialogActions, Button,  Box, Stack, Select, MenuItem, Typography, Divider, useTheme, styled } from '@mui/material';
import { TagsInput } from '../TagsInput';
import { EmailsInput } from '../EmailsInput/EmailsInput';
import { useInviteUser } from 'hooks/useInviteUser';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'hooks/useAuthContext';
import { UserRoles } from 'constants/userRoles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useGetInvitationRoles } from 'hooks/useGetInvitationRoles';
import { InviteButton, LanguageSelect, RoleSelect, StyledDivider } from './InviteUserDialog.styles';
import { createColorFilteredComponent } from 'helpers/styledHelpers';

const StyledMenuItem = createColorFilteredComponent<{ $color: string }>(MenuItem)(({ $color }) => ({
  color: $color
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12
  }
});

export const InviteUserDialog = () => {
  const [emails, setEmails] = useState<string[]>([]);
  const [invalidEmailsExist, setInvalidEmailsExist] = useState<boolean>(false);
  const [tags, setTags] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const { userState } = useAuthContext();
  const { invitationRoles } = useGetInvitationRoles();
  const { inviteUser, invitingUser } = useInviteUser();
  const { t, i18n } = useTranslation();
  const theme  = useTheme();
  
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setEmails([]);
    setTags([]);
    setMessage('');
    setSelectedRoleId(invitationRoles?.find(role => role.name === UserRoles.Participant)?.id || '');
    setSelectedLanguage(i18n.language);
    setOpen(false);
  };

  const handleEmailsChange = (emails: string[], invalidEmailsExist: boolean) => {
    setEmails(emails);
    setInvalidEmailsExist(invalidEmailsExist);
  };

  const handleTagsChange = (tags: string[]) => {
    setTags(tags);
  };

  const handleInviteClick = async () => {
    let tagA = '', tagB = '';
    if (tags.length === 1) {
      tagA = tags[0];
    } else if (tags.length === 2) {
      tagA = tags[0];
      tagB = tags[1];
    }
    const resp = await inviteUser({ emails, tagA, tagB, message, culture: selectedLanguage, roleId: selectedRoleId });
    if (resp?.success) {
        handleClose();
    }
  }

  useEffect(() => {
    if (invitationRoles) {
      setSelectedRoleId(invitationRoles.find(role => role.name === UserRoles.Participant)?.id || '');
    }
  }, [invitationRoles]);

  useEffect(() => {
    setSelectedLanguage(i18n.language);
  }, [i18n.language]);

  return (
    <Box>
      <InviteButton variant="outlined" color='secondary' onClick={handleOpen} data-testid="invite-user-button-navbar">
        {t('inviteUser.inviteUserButtonNavbar')}
      </InviteButton>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
        <Box maxHeight="calc(100vh - 200px)" overflow="auto">
          <Stack direction={'column'} paddingLeft={theme.spacing(10)} paddingRight={theme.spacing(10)} paddingTop={theme.spacing(5)} paddingBottom={theme.spacing(5)} spacing={theme.spacing(3)}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Stack>
                <Typography variant='h5' fontWeight={'600'}>
                    {t('inviteUser.inviteUserTitle')}
                </Typography>            
                <Typography variant='subtitle1' color={theme.palette.text.secondary}>
                    {t('inviteUser.inviteUserSubtitle', { organizationName: userState.user.selectedWorkspace.workspaceName })}
                </Typography>                
              </Stack>
              <Stack direction={'row'} alignItems={'center'}>
                  <Typography variant='subtitle1' fontWeight={500}>
                    {t('inviteUser.invitationLanguage')}:
                  </Typography>
                  <LanguageSelect
                    labelId="invitation-languages"
                    id="invitation-languages"
                    value={selectedLanguage} 
                    onChange={(e) => setSelectedLanguage(e.target.value as string)}
                    variant="outlined"
                    defaultValue={i18n.language}
                    IconComponent={KeyboardArrowDownIcon}
                  >
                    <StyledMenuItem value="en" $color={theme.palette.black.main}>EN</StyledMenuItem>
                    <StyledMenuItem value="pl" $color={theme.palette.black.main}>PL</StyledMenuItem>
                  </LanguageSelect>
                </Stack>
            </Stack>
          
          <Typography variant='subtitle2'>{t('inviteUser.enterAdresses')}</Typography>
          <EmailsInput onChange={handleEmailsChange}/>
          <Divider />
          <Stack display='flex' flexDirection={'row'}>
            <Stack flexDirection={'column'} display={'flex'} height='100%' width='50%' paddingTop={theme.spacing(2)} spacing={theme.spacing(2)}>
                <Typography variant='subtitle2'>{t('inviteUserDialog.selectUserFunction')}</Typography>
                {invitationRoles &&  <RoleSelect
                        labelId="invitation-roles-label"
                        id="invitation-roles-label"
                        value={selectedRoleId}
                        defaultValue={invitationRoles.find(role => role.name === UserRoles.Participant)?.id || ''}
                        onChange={(e) => setSelectedRoleId(e.target.value as string)}
                        variant='outlined'
                    >
                        {invitationRoles.map((role, index) => <MenuItem key={index} value={role.id}><Typography fontWeight={'500'}>{t(`inviteUserDialog.roles.${role.name}`)}</Typography></MenuItem>)}
                    </RoleSelect>}
            </Stack>              
            <Divider orientation='vertical'/>
            <Stack flexDirection={'column'} display={'flex'} height='100%' width='50%' paddingTop={theme.spacing(2)} paddingLeft={theme.spacing(4)} spacing={theme.spacing(2)}>
              <Typography variant='subtitle2'>{t('inviteUserDialog.addTags')}</Typography>
              <TagsInput onChange={handleTagsChange}/>
            </Stack>
          </Stack>
            <StyledDivider />
            <Typography variant='subtitle2'>{t('inviteUser.addMessage')}</Typography>
            <StyledTextField
              label={t('inviteUser.messageContent')}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={3}
            />
          </Stack>
        </Box>         
        </DialogContent>
        <DialogActions>
          <Stack paddingLeft={theme.spacing(10)} paddingRight={theme.spacing(10)} direction='row' spacing={theme.spacing(3)} marginBottom={theme.spacing(10)}>
            <Button onClick={handleClose} variant='outlined'>{t('general.cancel')}</Button>
            <LoadingButton 
              data-testid="invite-user-send-invite-button"
              loading={invitingUser} 
              disabled={emails.length === 0 || invalidEmailsExist} 
              onClick={handleInviteClick} 
              variant="contained" 
              color="primary">
              {t('inviteUser.sendInvite')}
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};